import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@apollo/client/react';

const DEFAULT_PER_PAGE = 25;

export default function usePaginatedList(listQuery, countQuery, extraVars = {}) {
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('id:asc');
  const perPage = extraVars.perPage ?? DEFAULT_PER_PAGE;

  const variables = useMemo(
    () => ({
      ...extraVars.baseVars,
      ...Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, v ? parseInt(v, 10) : null])
      ),
      sort,
      first: perPage,
      offset: (page - 1) * perPage
    }),
    [filters, page, sort, perPage, extraVars.baseVars]
  );

  const { data, loading, error, refetch } = useQuery(listQuery, {
    variables,
    notifyOnNetworkStatusChange: true
  });

  const countVars = useMemo(
    () => ({
      ...extraVars.baseVars,
      ...Object.fromEntries(
        Object.entries(filters).map(([k, v]) => [k, v ? parseInt(v, 10) : null])
      )
    }),
    [filters, extraVars.baseVars]
  );

  const { data: countData } = useQuery(countQuery, {
    variables: countVars
  });

  const listKey = listQuery.definitions?.[0]?.selectionSet?.selections?.[0]?.name?.value ?? 'list';
  const countKey =
    countQuery.definitions?.[0]?.selectionSet?.selections?.[0]?.name?.value ?? 'count';

  const total = countData?.[countKey] ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const listData = data?.[listKey];

  useEffect(() => {
    setPage(1);
  }, [filters, sort]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  return {
    filters,
    setFilters,
    page,
    setPage,
    sort,
    setSort,
    handleFilterChange,
    listData,
    loading,
    error,
    refetch,
    total,
    totalPages,
    perPage
  };
}
